import { DeleteModal } from "@/components/modal/DeleteModal.tsx";
import { useStandardMutation } from "@/modules/api/useStandardMutation.ts";
import type { DeleteVmMutation } from "@/modules/compute/VmView.tsx";
import type { VirtualMachineLoc } from "@/modules/compute/vmQuery.ts";
import { DeleteWithFloatingIp } from "@/modules/network/DeleteWithFloatingIp.tsx";
import { deleteIpAddressMutation, ipQueryAtom } from "@/modules/network/ipAddress/ipAddressApi.ts";
import { useSuspenseQueryAtom } from "@/utils/query/useSuspenseQueryAtom.ts";
import { Trans } from "@lingui/react/macro";
import { useState, type ReactNode } from "react";

export interface VmDeleteModalProps {
    vm: VirtualMachineLoc;
    deleteVmMutation: DeleteVmMutation;
}

export function VmDeleteMessage({ name }: { name: ReactNode }) {
    return (
        <>
            <p className="pb-3">
                <Trans>
                    Deleting the virtual machine "{name}" will permanently remove it, and it will not be accessible
                    after that.
                </Trans>
            </p>
            <p>
                <Trans>
                    Deleting the virtual machine will also delete your backups and snapshots, and the machine cannot be
                    restored after that. If you wish to keep the backups and snapshots consider stopping the machine
                    instead.
                </Trans>
            </p>
        </>
    );
}

export function VmDeleteModal({ vm, deleteVmMutation }: VmDeleteModalProps) {
    //#region Hooks
    const delIpMutation = useStandardMutation(deleteIpAddressMutation);

    const ips = useSuspenseQueryAtom(ipQueryAtom(vm.location));
    const attachedPublicIp = [...ips.values()].find(
        (ip) => ip?.assigned_to_resource_type === "virtual_machine" && ip.assigned_to === vm.uuid,
    );

    const [deleteFloatingIp, setDeleteFloatingIp] = useState<boolean>();
    //#endregion

    const { name } = vm;

    async function deleteAction() {
        await deleteVmMutation.mutateAsync({ uuid: vm.uuid, location: vm.location });
        if (deleteFloatingIp && attachedPublicIp) {
            await delIpMutation.mutateAsync({ location: vm.location, address: attachedPublicIp.address });
        }
    }

    return (
        <DeleteModal modalAction={deleteAction} isActionDisabled={!!attachedPublicIp && deleteFloatingIp === null}>
            <VmDeleteMessage name={name} />
            <DeleteWithFloatingIp
                ipAddress={attachedPublicIp}
                value={deleteFloatingIp}
                onChange={setDeleteFloatingIp}
            />
        </DeleteModal>
    );
}
